import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { userApi } from '../../api/userApi'
import { toast } from 'react-hot-toast'
import { ArrowLeft, Camera, X, Plus } from 'lucide-react'
import { Link } from 'react-router-dom'

const schema = z.object({
  displayName: z.string().min(2, 'Name must be at least 2 characters').max(100),
  bio: z.string().max(500).optional(),
  experienceYears: z.number().min(0).max(60).optional(),
  linkedinUrl: z.string().url('Invalid URL').optional().or(z.literal('')),
})

type FormValues = z.infer<typeof schema>

export default function EditProfilePage() {
  const navigate = useNavigate()
  const [skills, setSkills] = useState<string[]>([])
  const [skillInput, setSkillInput] = useState('')
  const [picFile, setPicFile] = useState<File | null>(null)
  const [picPreview, setPicPreview] = useState<string | null>(null)
  const [resumeFile, setResumeFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const picRef = useRef<HTMLInputElement>(null)
  const resumeRef = useRef<HTMLInputElement>(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) })

  useEffect(() => {
    userApi.getMe().then(({ data }) => {
      const p = data.data?.profile
      if (p) {
        reset({
          displayName: p.displayName || '',
          bio: p.bio || '',
          experienceYears: p.experienceYears,
          linkedinUrl: p.linkedinUrl || '',
        })
        setSkills(p.skills || [])
        if (p.profilePicUrl) setPicPreview(p.profilePicUrl)
      }
    })
  }, [reset])

  const handlePicChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setPicFile(file)
    setPicPreview(URL.createObjectURL(file))
  }

  const addSkill = () => {
    const s = skillInput.trim()
    if (s && !skills.includes(s) && skills.length < 30) {
      setSkills([...skills, s])
      setSkillInput('')
    }
  }

  const onSubmit = async (values: FormValues) => {
    setLoading(true)
    try {
      // Upload profile picture
      if (picFile) {
        const { data: urlData } = await userApi.getPresignedUpload('profile-pic', picFile.type)
        await fetch(urlData.data!.uploadUrl, {
          method: 'PUT',
          body: picFile,
          headers: { 'Content-Type': picFile.type },
        })
      }
      // Upload resume
      if (resumeFile) {
        const { data: urlData } = await userApi.getPresignedUpload('resume', resumeFile.type)
        await fetch(urlData.data!.uploadUrl, {
          method: 'PUT',
          body: resumeFile,
          headers: { 'Content-Type': resumeFile.type },
        })
      }
      // Update profile
      await userApi.updateMe({ ...values, skills })
      toast.success('Profile updated!')
      navigate('/profile')
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update profile')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto">
      <Link to="/profile" className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4 no-underline">
        <ArrowLeft size={16} /> Back to Profile
      </Link>

      <div className="card">
        <h1 className="text-xl font-bold text-gray-900 mb-6">Edit Profile</h1>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Profile Picture */}
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-primary-100 flex items-center justify-center">
                {picPreview ? (
                  <img src={picPreview} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-2xl font-bold text-primary-600">?</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => picRef.current?.click()}
                className="absolute bottom-0 right-0 w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center"
              >
                <Camera size={12} className="text-white" />
              </button>
            </div>
            <input ref={picRef} type="file" accept="image/*" className="hidden" onChange={handlePicChange} />
            <p className="text-sm text-gray-500">Click the camera icon to update your photo</p>
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Display Name *</label>
            <input {...register('displayName')} className="input" />
            {errors.displayName && <p className="text-xs text-red-500 mt-1">{errors.displayName.message}</p>}
          </div>

          {/* Bio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Bio</label>
            <textarea {...register('bio')} rows={3} className="input resize-none" placeholder="Tell others about yourself..." />
          </div>

          {/* Experience */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Years of Experience</label>
            <input
              type="number"
              {...register('experienceYears', { valueAsNumber: true })}
              className="input w-32"
              min={0}
              max={60}
            />
          </div>

          {/* LinkedIn */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">LinkedIn URL</label>
            <input {...register('linkedinUrl')} className="input" placeholder="https://linkedin.com/in/..." />
            {errors.linkedinUrl && <p className="text-xs text-red-500 mt-1">{errors.linkedinUrl.message}</p>}
          </div>

          {/* Skills */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Skills</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                className="input flex-1"
                placeholder="Add a skill"
              />
              <button type="button" onClick={addSkill} className="btn-secondary px-3">
                <Plus size={16} />
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {skills.map((skill) => (
                <span key={skill} className="badge badge-gray flex items-center gap-1">
                  {skill}
                  <button type="button" onClick={() => setSkills(skills.filter((s) => s !== skill))}>
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Resume */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Resume</label>
            <input ref={resumeRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setResumeFile(e.target.files?.[0] || null)} />
            <button type="button" onClick={() => resumeRef.current?.click()} className="btn-secondary text-sm">
              {resumeFile ? resumeFile.name : 'Upload Resume'}
            </button>
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full">
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>
    </div>
  )
}
